import { ColumnHelper } from "@tanstack/react-table";
import { ManageRolesTableDataItem } from "PFApp/activities/manage_roles/manage_roles.types";
import { useInteractiveFormHandlers } from "PFApp/components/activity_form/multi_activity_form/tabular_roles_form/hooks/use_interactive_form_handlers";
import { useRolesTableColumns } from "PFApp/components/activity_form/multi_activity_form/tabular_roles_form/hooks/use_roles_table_columns";
import css from "PFApp/components/activity_form/multi_activity_form/tabular_roles_form/tabular_roles_form.module.scss";
import {
  FIELD_AVAILABILITY_KEY,
  FIELD_CONVERTED_AVAILABILITY_KEY,
  FIELD_MARKED_FOR_REMOVAL_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useMultiActivityFormStore } from "PFApp/stores/activity_form/multi_roles_form";
import { useManageRolesStore } from "PFApp/stores/manage_roles";
import { RolesFormKey } from "PFApp/types/activity_form/activity_form_schema";
import {
  ManageRolesFormType,
  ManageRolesMultiForm
} from "PFApp/types/activity_form/manage_roles_form_schema";
import Table, { DragAxis, InteractiveTable } from "PFComponents/table";
import { Subtemplate } from "PFCore/types";
import { useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";

import { useFormPendingEditsTracker } from "../hooks/use_form_pending_edits_tracker";
import { useManageRolesColumns } from "../hooks/use_manage_roles_columns";
import { getTrackedKeysFromSchemaFields, mergeManageRolesColumns } from "../manage_roles.utils";

type ManageRolesFormProps = {
  subtemplate: Subtemplate | null;
  rolesFormKey: RolesFormKey;
};

export const ManageRolesForm = ({ subtemplate, rolesFormKey }: ManageRolesFormProps) => {
  const scrollableContainerRef = useMultiActivityFormStore((store) => store.scrollableContainerRef);
  const currentPage = useManageRolesStore((state) => state.currentPage);

  const tableRowsInfo = useWatch<ManageRolesMultiForm, RolesFormKey, unknown, ManageRolesTableDataItem[]>({
    name: rolesFormKey,
    compute: (roles = []) =>
      (roles as (ManageRolesFormType & { markedForRemoval?: boolean })[]).map((role) => ({
        uniqueId: role.uniqueId,
        disabled: !!role[FIELD_MARKED_FOR_REMOVAL_KEY],
        engagement:
          role.engagement_id !== undefined && role.engagement_name !== undefined
            ? { id: role.engagement_id as number, name: role.engagement_name }
            : undefined,
        activityId: role.id
      }))
  });

  const rolesCount = tableRowsInfo.length;
  const engagements = tableRowsInfo.map((row) => row.engagement);

  const tableInstanceRef = useRef(null);

  const {
    generateColumns: generateSchemaColumns,
    hiddenFormControllers,
    formSchemaFields,
    getFieldPath,
    getFieldByColumnId,
    getCellBehavior
  } = useRolesTableColumns({
    subtemplate,
    rolesFormKey,
    rolesCount,
    tableInstanceRef,
    isReadonly: false
  });

  const { handleFill, handlePaste, handleClear } = useInteractiveFormHandlers({
    tableInstanceRef,
    rolesFormKey,
    tableRowsInfo,
    getFieldPath,
    getFieldByColumnId,
    getCellBehavior
  });

  const trackedKeys = useMemo(() => {
    const schemaKeys = getTrackedKeysFromSchemaFields(formSchemaFields);
    schemaKeys.add(FIELD_AVAILABILITY_KEY);
    schemaKeys.add(FIELD_CONVERTED_AVAILABILITY_KEY);
    return schemaKeys;
  }, [formSchemaFields]);

  useFormPendingEditsTracker({ rolesFormKey, trackedKeys, currentPage });

  const { generateColumns: generateCustomColumns } = useManageRolesColumns({
    rolesFormKey,
    engagements
  });

  const generateMergedColumns = (columnHelper: ColumnHelper<ManageRolesTableDataItem>) => {
    const schemaColumns = generateSchemaColumns(columnHelper);
    const customColumns = generateCustomColumns(columnHelper);

    return mergeManageRolesColumns(schemaColumns, customColumns);
  };

  return (
    <>
      {hiddenFormControllers}
      <InteractiveTable
        dragAxis={DragAxis.Vertical}
        tableInstanceRef={tableInstanceRef}
        restrictPasteToSourceColumn
        onFill={handleFill}
        onPaste={handlePaste}
        onClear={handleClear}
        scrollableContainerRef={scrollableContainerRef}
      >
        <Table
          generateColumns={generateMergedColumns}
          tableData={tableRowsInfo}
          className={css.table}
          enableSorting={false}
          tableInstanceRef={tableInstanceRef}
          scrollableContainerRef={scrollableContainerRef}
          hideDistantRows
          noHover
          displayGrid
          disableInternalScrolling
          disableAutoResizing
          isInteractive
        />
      </InteractiveTable>
    </>
  );
};
